/**
 * Status pills.
 *
 * WHY the labels are the raw API enums: RECONCILED / DISCREPANT / INCOMPLETE, ALERT / WARN /
 * INFO and the discrepancy kinds are the product's vocabulary (docs/reference/CONSTANTS.md).
 * Prettifying them here would create a second, drifting set of names for the same facts, so
 * only the colour is ours.
 */
import type { ReactNode } from "react";
import type { ReconcileResult, Severity } from "../api";

type Tone = "neutral" | "good" | "warn" | "bad" | "info";

const RESULT_TONE: Record<ReconcileResult, Tone> = { RECONCILED: "good", DISCREPANT: "bad", INCOMPLETE: "warn" };
const SEVERITY_TONE: Record<Severity, Tone> = { ALERT: "bad", WARN: "warn", INFO: "info" };
/** Connection + batch + line + dispute statuses that deserve a colour; everything else is neutral. */
const STATUS_TONE: Readonly<Record<string, Tone>> = {
  ACTIVE: "good", PAID: "good", SETTLED: "good", RESOLVED: "good", WON: "good", RELEASED: "good", ACCEPTED: "neutral",
  OPEN: "info", SCHEDULED: "info", IN_TRANSIT: "info", PENDING: "info", ESTIMATED: "info", UNDER_REVIEW: "info",
  DEFERRED: "warn", HELD: "warn", NEEDS_RESPONSE: "bad", TOKEN_EXPIRED: "warn", ACKNOWLEDGED: "warn",
  FAILED: "bad", CANCELED: "bad", LOST: "bad", DEAUTHORIZED: "bad", PENDING_AUTH: "warn", IGNORED: "neutral",
};

export function Badge({ tone = "neutral", children, title }: { tone?: Tone; children: ReactNode; title?: string }): ReactNode {
  return <span className={`badge badge-${tone}`} title={title}>{children}</span>;
}

export function ResultBadge({ result }: { result: ReconcileResult | null }): ReactNode {
  if (!result) return <Badge tone="neutral" title="No reconciliation run has covered this batch yet">NOT RUN</Badge>;
  return <Badge tone={RESULT_TONE[result]}>{result}</Badge>;
}

export function SeverityBadge({ severity }: { severity: Severity }): ReactNode {
  return <Badge tone={SEVERITY_TONE[severity]}>{severity}</Badge>;
}

export function StatusBadge({ status }: { status: string | null }): ReactNode {
  if (!status) return null;
  return <Badge tone={STATUS_TONE[status] ?? "neutral"}>{status}</Badge>;
}
